import { useId } from 'react';

import Text from './Text';

/**
 * Champ de saisie du socle mobile — #34, #79.
 *
 * Trois garanties par construction, comme `Button` :
 *
 * - **Zone tactile de 44 px** (`min-h-touch`).
 * - **Libellé accessible obligatoire.** `libelle` est requis par le typage, donc
 *   `tsc --noEmit` refuse un champ anonyme. C'est le typage qui remplace ici la
 *   règle de lint (#34) : `mobile:check` sait voir un `<button>` nu, il ne sait
 *   pas lire un libellé.
 * - **Taille de police ≥ 16 px** (`text-base`). Ce n'est pas une préférence
 *   esthétique : sous 16 px, iOS Safari ZOOME automatiquement à la mise au point
 *   du champ, et ne dézoome jamais tout seul. L'utilisateur se retrouve avec une
 *   page agrandie qu'il doit repincer à la main, après chaque champ. C'est le
 *   défaut le plus visible qu'on puisse laisser dans un formulaire mobile.
 *
 * Le libellé est VISIBLE, pas seulement lu. Un placeholder qui tient lieu de
 * libellé disparaît à la première frappe : on perd le nom du champ au moment
 * précis où on essaie de le remplir, et un lecteur d'écran qui revient en arrière
 * ne trouve plus rien.
 */
interface InputProps {
    /** Texte visible ET nom accessible : les deux ne divergent jamais. */
    libelle: string;
    valeur: string;
    onChange: (valeur: string) => void;
    type?: 'text' | 'email' | 'password' | 'search';
    /**
     * Renseigne le trousseau du navigateur. `username` / `current-password` sont
     * ce qu'iOS et les gestionnaires de mots de passe attendent pour proposer le
     * remplissage — sans eux, quelqu'un qui a un mot de passe fort doit le taper
     * au pouce.
     */
    autoComplete?: string;
    inputMode?: 'text' | 'email' | 'numeric';
    /** Message d'erreur sous le champ, annoncé au lecteur d'écran. */
    erreur?: string;
    disabled?: boolean;
    autoFocus?: boolean;
}

export default function Input({
    libelle,
    valeur,
    onChange,
    type = 'text',
    autoComplete,
    inputMode,
    erreur,
    disabled = false,
    autoFocus = false,
}: InputProps) {
    // `useId` plutôt qu'un identifiant passé en prop : deux champs du même
    // formulaire ne peuvent pas collider, et l'appelant n'a rien à inventer.
    const id = useId();
    const idErreur = `${id}-erreur`;

    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={id}>
                <Text variante="petit" ton="attenue">
                    {libelle}
                </Text>
            </label>

            <input
                id={id}
                type={type}
                value={valeur}
                onChange={(e) => onChange(e.target.value)}
                autoComplete={autoComplete}
                inputMode={inputMode}
                disabled={disabled}
                autoFocus={autoFocus}
                aria-invalid={erreur ? true : undefined}
                aria-describedby={erreur ? idErreur : undefined}
                className={`min-h-touch w-full rounded-xl border bg-white px-4 py-3 text-base text-text-color ${
                    erreur ? 'border-alert-text-color' : 'border-border-color-light'
                } disabled:opacity-60`}
            />

            {erreur && (
                // `role="alert"` : le message apparaît APRÈS une tentative, donc il
                // doit être annoncé sans que l'utilisateur ait à repartir le chercher.
                <Text variante="petit" ton="danger" as="span" className="block" >
                    <span id={idErreur} role="alert">
                        {erreur}
                    </span>
                </Text>
            )}
        </div>
    );
}
